/** Normalize birthday / wedding text to YYYY-MM-DD for the Worker. */

/**
 * Live-format typed/pasted input as MM/DD/YYYY.
 * Digits only is fine — slashes are inserted as you type.
 */
export function formatUsDateInput(raw: string): string {
  const trimmed = raw.trim();
  const iso = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (iso) {
    return `${iso[2].padStart(2, "0")}/${iso[3].padStart(2, "0")}/${iso[1]}`;
  }

  let digits = raw.replace(/\D/g, "").slice(0, 8);
  // Paste of YYYYMMDD (e.g. 19670504) → show as US date.
  if (
    digits.length === 8 &&
    /^(19|20)\d{2}/.test(digits) &&
    formatValidYmd(Number(digits.slice(0, 4)), Number(digits.slice(4, 6)), Number(digits.slice(6, 8)))
  ) {
    digits = `${digits.slice(4, 6)}${digits.slice(6, 8)}${digits.slice(0, 4)}`;
  }

  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function formatValidYmd(year: number, month: number, day: number): string | null {
  if (!year || !month || !day) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** Parse digit-only dates people type without slashes (e.g. 05041967). */
function normalizeDigitOnlyDate(digits: string): string | null {
  if (digits.length === 8) {
    // Prefer MM/DD/YYYY (matches the UI copy), then YYYY-MM-DD.
    const us = formatValidYmd(
      Number(digits.slice(4)),
      Number(digits.slice(0, 2)),
      Number(digits.slice(2, 4)),
    );
    if (us) return us;
    return formatValidYmd(
      Number(digits.slice(0, 4)),
      Number(digits.slice(4, 6)),
      Number(digits.slice(6, 8)),
    );
  }
  if (digits.length === 7) {
    // M/DD/YYYY without a leading zero on the month — e.g. 5041967.
    return formatValidYmd(
      Number(digits.slice(3)),
      Number(digits.slice(0, 1)),
      Number(digits.slice(1, 3)),
    );
  }
  return null;
}

export function normalizeBirthDateInput(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const iso = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (iso) {
    return formatValidYmd(Number(iso[1]), Number(iso[2]), Number(iso[3]));
  }

  const us = trimmed.match(/^(\d{1,2})[/.\-\s]+(\d{1,2})[/.\-\s]+(\d{4})$/);
  if (us) {
    return formatValidYmd(Number(us[3]), Number(us[1]), Number(us[2]));
  }

  // Digits only (or digits with spaces): 05041967, 05 04 1967, 5041967.
  if (/^[\d\s./-]+$/.test(trimmed)) {
    return normalizeDigitOnlyDate(trimmed.replace(/\D/g, ""));
  }

  return null;
}
